import type React from "react";
import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import type { AppDispatch, RootState } from "../store";
import { fetchCities } from "../store/features/citySlice";
import { searchTrucks } from "../store/features/truckSlice";
import TruckTable from "../components/trucks/TruckTable";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { TruckSearchFilters } from "../types/truck";
import { createTruckApi } from "../api/trucksApi";
import { getErrorMessage } from "../utils/errorHandling";
import { toastEmitter } from "../utils/toastEmitter";
import type { PaginationParam } from "../types/paginationResponse";
import { setCurrentPage } from "../store/features/appStateSlice";
import { isUserAdmin } from "../types/user";

const TRUCK_PAGE_SIZE_KEY = "truckPageSize";
const DEFAULT_PAGE_SIZE = 10;

type TranslationKeys = keyof typeof translations.en;

interface DefaultTruckGroupProps {
  defaultType: "WINGBOX" | "LOSBAK" | "DUMP";
  title: string;
  hideTypeFilter?: boolean;
}

const DefaultTruckGroup: React.FC<DefaultTruckGroupProps> = ({
  defaultType,
  title,
  hideTypeFilter,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const { trucks, loading, error, pagination } = useSelector((state: RootState) => state.trucks);
  const { cities } = useSelector((state: RootState) => state.cities);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const isAdmin = isUserAdmin(user);

  // Form state
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Search state - initialize once from URL params
  const [filters, setFilters] = useState<TruckSearchFilters>({
    licensePlate: searchParams.get("licensePlate") || "",
    status: searchParams.get("status") || "",
    cityId: searchParams.get("cityId") || "",
    type: defaultType, // Set default type
    sortBy: searchParams.get("sortBy") || "",
    sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "asc",
  });

  const [paginationState, setPaginationState] = useState<PaginationParam>(() => {
    const savedPageSize = localStorage.getItem(TRUCK_PAGE_SIZE_KEY);
    return {
      page: Number(searchParams.get("page")) || 1,
      pageSize: savedPageSize ? Number.parseInt(savedPageSize, 10) : DEFAULT_PAGE_SIZE,
    };
  });

  // Initial data loading
  useEffect(() => {
    dispatch(fetchCities());
    dispatch(setCurrentPage({ page: "trucks", metadata: { type: defaultType } }));
  }, [dispatch, defaultType]);

  // Handle URL params and search
  useEffect(() => {
    dispatch(searchTrucks({ filters, pagination: paginationState }));
  }, [dispatch, filters, paginationState]);

  // Form handlers
  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setAddError(null);
  };

  const handleSubmit = async (truckData: {
    licensePlate: string;
    type: string;
  }) => {
    try {
      await createTruckApi(truckData);
      await dispatch(
        searchTrucks({ filters: { type: defaultType }, pagination: { page: 1, pageSize: 10 } }),
      ).unwrap();
      handleClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createTruck");
      setAddError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  // Table handlers
  const handlePaginationChange = (newPagination: PaginationParam) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setPaginationState(newPagination);
    localStorage.setItem(TRUCK_PAGE_SIZE_KEY, newPagination.pageSize.toString());
  };

  const handleSearch = async (newFilters: TruckSearchFilters, newPagination: PaginationParam) => {
    const newParams = new URLSearchParams();
    for (const [key, value] of Object.entries(newFilters)) {
      if (value) {
        newParams.set(key, value.toString());
      }
    }
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setFilters(newFilters);
    setPaginationState(newPagination);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{title}</Typography>
      </Box>

      <TruckTable
        trucks={trucks}
        cities={cities}
        onSearch={handleSearch}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        loading={loading}
        error={error}
        hideTypeFilter={hideTypeFilter}
      />
    </Box>
  );
};

export default DefaultTruckGroup;
